import { historyField } from '@codemirror/commands';
import type { EditorState, StateField } from '@codemirror/state';
import type { ViewUpdate } from '@codemirror/view';
import type { Code } from '@edu-frontend/modules/src/shared/ui/Code';
import type { CodeHistory } from '@yandex-int/k-common';
import React from 'react';

import { checkHistoryConsistency, historyFromRaw, historyToRaw } from './code-history';
import { CodePasteTracker, CodePasteTrackedState } from './code-pastes';

export type InitialCodeState = readonly [string, React.ComponentProps<typeof Code>['initialState']?];

export function useInitialCodeHistory(
    initialValue: () => string | CodeHistory,
    pasteTracker: CodePasteTracker
): InitialCodeState {
    return React.useMemo((): InitialCodeState => {
        const textOrHistory = typeof initialValue === 'string' ? initialValue : initialValue();
        if (typeof textOrHistory === 'string' || typeof textOrHistory === 'undefined') {
            return [textOrHistory];
        }

        const consistency = checkHistoryConsistency(textOrHistory);
        switch (consistency.type) {
            case 'consistent': {
                const rawState = historyToRaw(textOrHistory);
                pasteTracker.importPastes(textOrHistory);
                return [
                    rawState.doc,
                    {
                        json: rawState,
                        fields: getHistoryStateFields(),
                    },
                ];
            }
            case 'inconsistent': {
                const message = 'Inconsistent code editor history to initialize with';
                console.error(message, textOrHistory);
                window.Ya?.Rum?.logError?.({ level: 'warn', message });
                return [textOrHistory.lastText];
            }
            case 'error': {
                const message = 'Error checking code editor history consistency to initialize with';
                console.error(message, textOrHistory);
                window.Ya?.Rum?.logError?.({ level: 'error', message }, consistency.error as Error);
                return [textOrHistory.lastText];
            }
            default: {
                return [textOrHistory.lastText];
            }
        }
    }, [initialValue, pasteTracker]);
}

export function useOnChangeHistory(
    pasteTracker: CodePasteTracker,
    onChange?: (text: string) => void,
    onChangeHistory?: (history: LazyCodeHistory) => void
): React.ComponentProps<typeof Code>['onChange'] {
    const callback = React.useCallback(
        (text: string, update: ViewUpdate) => {
            onChange?.(text);

            if (onChangeHistory) {
                onChangeHistory(new LazyCodeHistory(update.state, pasteTracker.export()));
            }
        },
        [onChange, onChangeHistory, pasteTracker]
    );
    return onChange || onChangeHistory ? callback : undefined;
}

export class LazyCodeHistory {
    private cached: CodeHistory | undefined;

    constructor(
        private readonly editorState: EditorState,
        private readonly pasteState: CodePasteTrackedState
    ) {}

    getHistory(): CodeHistory {
        if (!this.cached) {
            const rawState = this.editorState.toJSON(getHistoryStateFields());
            this.cached = this.pasteState.applyPastes(historyFromRaw(rawState));
        }
        return this.cached;
    }
}

function getHistoryStateFields(): Record<string, StateField<any>> {
    return {
        history: historyField,
    };
}
